<script setup lang="ts">
import { computed, reactive, ref, toRaw } from 'vue';
import { watchDebounced } from '@vueuse/core';
import _isEqual from 'lodash/isEqual';

import KMultipleSelectField from '@/components/fields/KMultipleSelectField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import DispatchDriverFiltersFieldset from '@/components/fieldsets/DispatchDriverFiltersFieldset.vue';
import useForm from '@/composables/useForm';
import { useStaticStore } from '@/stores/useStaticStore';

export interface InitialData {
  search?: string | null;
  fleets?: string[] | null;
  driverFilters: {
    type?: string[] | null;
    zone?: string[] | null;
  };
}

export interface ValidData {
  search?: string | null;
  fleets?: string[] | null;
  driverFilters: {
    type?: string[] | null;
    zone?: string[] | null;
  };
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({
    driverFilters: {},
  }),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({
  driverFilters: {},
});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

watchDebounced(
  [
    () => formData.search,
    () => formData.fleets,
    () => formData.driverFilters.type,
    () => formData.driverFilters.zone,
  ],
  async (newValues, oldValues) => {
    if (!_isEqual(newValues, oldValues)) {
      await handleSubmit();
    }
  },
  { deep: true, debounce: 500 },
);

const staticStore = useStaticStore();

const dispatchFleetsItems = computed(() => {
  return staticStore.getFleets
    .filter((fleet) => {
      return !!fleet.department;
    })
    .map((fleet) => {
      return {
        // TODO as string
        value: fleet.department as string,
        // TODO as string
        title: fleet.department as string,
      };
    });
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.dispatchDriverSearchForm"
    data-testid="dispatchDriverSearchForm"
    @submit.prevent="handleSubmit"
  >
    <div :class="$style.leftColumn">
      <KTextField
        v-model="formData.search"
        label="Search"
        :class="$style.field"
        placeholder="Type to search..."
        clearable
        hide-details
      />
    </div>
    <div :class="$style.rightColumn">
      <KMultipleSelectField
        v-model="formData.fleets"
        label="Fleets"
        :class="[$style.field, $style.fleetsSelectField]"
        :items="dispatchFleetsItems"
        :plusCount="1"
        hide-details
        @keydown.enter="handleSubmit"
      />
      <DispatchDriverFiltersFieldset
        v-model="formData.driverFilters"
        :class="$style.driverFiltersFieldset"
      />
    </div>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchDriverSearchForm {
  display: flex;
}
  .leftColumn {
    margin-right: auto;
  }
  .rightColumn {
    text-align: right;

    .field {
      margin-left: 1.5rem;
    }
  }
    .field {
      width: 180px;
      display: inline-block;
      vertical-align: top;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    .fleetsSelectField {
      width: 160px;
    }
    .driverFiltersFieldset {
      display: inline-block;
      margin-left: 1.5rem;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
</style>
